import { useState } from 'react';
import { Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useGetListing, useGetListingVersions } from '@/resources/listing';
import type { ListingVersionItem } from '@/types/listing';
import type { Product } from '@/types/product';
import { bulletHasVisibleContent, DiffText, hasDiffChanges } from './DiffText';
import {
  ListingGalleryMainImage,
  ListingGalleryThumbnails,
  useListingGallery,
} from './ListingImageGallery';
import { formatMoney, formatRating, formatReviews } from './listingFormat';
import { groupVersionsByElapsedTime } from './versionGrouping';

export interface ListingVersionHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  brandId: number;
  asin: string;
  productTitle: string;
  /**
   * The same current price, rating and review count the panel shows. They
   * are not versioned -- every version renders the same figures, which is
   * the design's own ruling and why `ListingVersionItem` carries none of
   * them (`components/product.md § The ruling on price, rating and review
   * count — do not reopen`; `AUT-303`).
   */
  heroShow: Pick<Product, 'price' | 'rating' | 'reviews'>;
}

const SECTION_HEADING_CLASSES = 'text-base font-semibold leading-6 text-muted-foreground';

/**
 * The line a version with nothing to report carries in the list. `summary`
 * is null in two cases -- an all-`same` diff, which is a fact the reader
 * already holds, and a failed Gemini call, which is not. Only the first is
 * answerable here, so the fallback is gated on the diff rather than applied
 * to every null (`AUT-304`).
 */
const NO_CHANGES_SUMMARY = 'No changes in this version.';

const summaryLineFor = (version: ListingVersionItem): string | null => {
  if (version.summary !== null) return version.summary;
  return hasDiffChanges(version.diff.title, version.diff.bullets) ? null : NO_CHANGES_SUMMARY;
};

/**
 * `Timestamp format` (`design-input/listing-content-panel/prototype-
 * extracted.md`): `en-US`, `{ month: "short", day: "numeric", year:
 * "numeric" }` then `{ hour: "numeric", minute: "2-digit" }`, joined by a
 * single space -- `Jul 28, 2026 3:04 PM`. `captured_at` is ISO 8601 UTC on
 * the wire; `Date` renders it in the browser's local zone
 * (`components/product.md § Time on this screen`).
 */
const formatVersionTimestamp = (capturedAt: string): string => {
  const date = new Date(capturedAt);
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} ${timePart}`;
};

/**
 * The version history modal (`WEB-003`, `specs/product/listing-content-
 * panel`, `AUT-231`). Owns its own versions request -- `WEB-004`'s panel
 * only owns `open` and passes it straight through as this hook's
 * `enabled` argument, so a closed modal issues no request (`WEB-001`).
 *
 * Layout follows the prototype: the content pane on the left with the
 * `Highlight changes` switch in its own footer, the version list on the
 * right with `Close` in its own footer (`AUT-303`).
 *
 * `Images` is deliberately read from the `useGetListing` cache and never
 * from a version row: the gallery is current, not versioned
 * (`components/product.md § The three routes`), so it does not change
 * when a different version is selected -- but it is selectable, so the
 * reader can look at any of the images while reading a version
 * (`AUT-301`). That read is gated on `open` for the same reason the
 * versions request is: this component is mounted for as long as the panel
 * is, so an ungated hook here would issue a second `GET …/listing` on
 * every product view -- including one behind a collapsed drawer, defeating
 * the panel's own gate.
 */
export function ListingVersionHistoryModal({
  open,
  onOpenChange,
  productId,
  brandId,
  asin,
  productTitle,
  heroShow,
}: ListingVersionHistoryModalProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [highlight, setHighlight] = useState(true);

  const { data: versionsData } = useGetListingVersions(productId, brandId, open);
  const { data: listingData } = useGetListing(productId, brandId, open);

  const versions: ListingVersionItem[] = versionsData?.versions ?? [];
  const images: string[] = listingData?.image_urls ?? [];
  const gallery = useListingGallery(images);
  const groups = groupVersionsByElapsedTime(versions);
  const latestVersionId = versions[0]?.id ?? null;

  const selectedVersion =
    versions.find((version) => version.id === selectedVersionId) ?? versions[0] ?? null;

  const noChanges = selectedVersion
    ? !hasDiffChanges(selectedVersion.diff.title, selectedVersion.diff.bullets)
    : false;

  const versionCount = versions.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0"
        data-testid="listing-version-history-modal"
      >
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="pr-8">
            Version history for{' '}
            <span className="text-muted-foreground font-normal">{productTitle}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {asin} · {versionCount} {versionCount === 1 ? 'version' : 'versions'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
              {selectedVersion && (
                <>
                  <Badge variant="secondary">
                    {formatVersionTimestamp(selectedVersion.captured_at)}
                  </Badge>

                  <section className="space-y-3">
                    <h3 className={SECTION_HEADING_CLASSES}>Images</h3>
                    <div className="overflow-hidden rounded-sm border border-border bg-card">
                      <ListingGalleryMainImage
                        src={gallery.mainImage}
                        alt={productTitle}
                        className="max-h-[320px]"
                      />
                    </div>
                    <ListingGalleryThumbnails
                      images={images}
                      selectedIndex={gallery.selectedIndex}
                      onSelect={gallery.select}
                    />
                  </section>

                  <section className="space-y-2">
                    <h3 className={SECTION_HEADING_CLASSES}>Title</h3>
                    <p data-testid="listing-version-title-diff" className="text-sm leading-relaxed">
                      <DiffText segments={selectedVersion.diff.title} highlight={highlight} />
                    </p>
                  </section>

                  <div className="flex items-center gap-4" data-testid="listing-version-price-rating">
                    <span className="text-base font-semibold text-foreground">
                      {formatMoney(heroShow.price)}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Star className="h-3.5 w-3.5 fill-foreground text-foreground" strokeWidth={0} />
                      <span className="font-medium text-foreground">{formatRating(heroShow.rating)}</span>
                      <span>({formatReviews(heroShow.reviews)})</span>
                    </span>
                  </div>

                  <section className="space-y-2">
                    <h3 className={SECTION_HEADING_CLASSES}>About this item</h3>
                    <ul data-testid="listing-version-bullets-diff" className="space-y-3">
                      {selectedVersion.diff.bullets.map((bulletSegments, index) => {
                        if (!bulletHasVisibleContent(bulletSegments, highlight)) return null;
                        return (
                          <li key={index} className="flex gap-2 text-sm leading-relaxed">
                            <span
                              aria-hidden="true"
                              className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-muted-foreground"
                            />
                            <span>
                              <DiffText segments={bulletSegments} highlight={highlight} />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-border px-6 py-3">
              <Switch
                id="highlight-changes"
                data-testid="listing-highlight-changes-switch"
                checked={highlight}
                onCheckedChange={setHighlight}
              />
              <Label htmlFor="highlight-changes" className="text-sm">
                Highlight changes
              </Label>
              {noChanges && (
                <span className="text-xs text-muted-foreground">No changes in this version</span>
              )}
            </div>
          </div>

          <Separator orientation="vertical" className="h-auto" />

          <div className="flex w-[280px] shrink-0 flex-col">
            <div
              className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3"
              data-testid="listing-version-list"
            >
              {groups.map((group, groupIndex) => (
                <div key={`${group.label}-${groupIndex}`} className="space-y-1">
                  <div className="px-2 text-xs text-muted-foreground">{group.label}</div>
                  {group.versions.map((version) => {
                    const summaryLine = summaryLineFor(version);

                    return (
                      <button
                        key={version.id}
                        type="button"
                        data-testid={`listing-version-row-${version.id}`}
                        onClick={() => setSelectedVersionId(version.id)}
                        className={cn(
                          'w-full rounded-lg px-2 py-2 text-left transition-colors',
                          selectedVersion?.id === version.id ? 'bg-muted' : 'hover:bg-muted/50',
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-semibold">
                            {formatVersionTimestamp(version.captured_at)}
                          </span>
                          {version.id === latestVersionId && (
                            <Badge variant="secondary" className="shrink-0">
                              Latest
                            </Badge>
                          )}
                        </div>
                        {summaryLine !== null && (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {summaryLine}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end border-t border-border p-3">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
